import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { compressImage, fileToBase64 } from '../../utils/imageCompression';
import type {
  PickupPhoto,
  UploadPickupPhotoRequest,
  UploadPickupPhotoResponse,
  DeletePickupPhotoRequest,
  DeletePickupPhotoResponse,
  ListPickupPhotosRequest,
  ListPickupPhotosResponse,
} from '@shared/types';
import type { AuthorizedPerson } from '@shared/types/declarationData';

interface Props {
  signatureId: string;
  authorizedPersons: AuthorizedPerson[];
}

const MAX_SIZE_BYTES = 300 * 1024; // 300KB — backend limit

export function PickupPhotosSection({ signatureId, authorizedPersons }: Props) {
  const { t } = useTranslation();

  // Map personIndex → PickupPhoto
  const [photos, setPhotos] = useState<Map<number, PickupPhoto>>(new Map());
  // personIndex → uploading state
  const [uploading, setUploading] = useState<Set<number>>(new Set());
  // pickupPhotoId → deleting state
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  // Full-size modal
  const [fullSizeUrl, setFullSizeUrl] = useState<string | null>(null);
  // Confirm delete modal
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Per-person error messages
  const [errors, setErrors] = useState<Map<number, string>>(new Map());

  // Hidden file inputs — one per person × 2 (upload + camera)
  // Camera button is only shown on touch devices (mobile) because <input capture>
  // only meaningfully opens the camera there; on desktop it just opens a file picker.
  const uploadRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const cameraRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const [isTouchDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  });

  // Load existing photos on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const listFn = httpsCallable<ListPickupPhotosRequest, ListPickupPhotosResponse>(
          functions,
          'listPickupPhotos'
        );
        const result = await listFn({ signatureId });
        if (cancelled) return;
        const map = new Map<number, PickupPhoto>();
        for (const photo of result.data.photos) {
          map.set(photo.personIndex, photo);
        }
        setPhotos(map);
      } catch {
        // Non-critical — photos just won't pre-load
      }
    };
    load();
    return () => { cancelled = true; };
  }, [signatureId]);

  function setError(personIndex: number, msg: string) {
    setErrors((prev) => {
      const next = new Map(prev);
      next.set(personIndex, msg);
      return next;
    });
    setTimeout(() => {
      setErrors((prev) => {
        const next = new Map(prev);
        next.delete(personIndex);
        return next;
      });
    }, 4000);
  }

  async function handleFileSelected(personIndex: number, file: File | null) {
    if (!file) return;

    const person = authorizedPersons[personIndex];
    if (!person) return;

    // Mark uploading
    setUploading((prev) => new Set(prev).add(personIndex));
    setErrors((prev) => { const n = new Map(prev); n.delete(personIndex); return n; });

    try {
      // Compress client-side
      const compressed = await compressImage(file);

      // Safety check
      if (compressed.size > MAX_SIZE_BYTES) {
        setError(personIndex, t('parent.pickupPhotos.imageTooLarge'));
        return;
      }

      const base64 = await fileToBase64(compressed);

      const uploadFn = httpsCallable<UploadPickupPhotoRequest, UploadPickupPhotoResponse>(
        functions,
        'uploadPickupPhoto'
      );

      const result = await uploadFn({
        signatureId,
        personIndex: personIndex as 0 | 1 | 2,
        personName: person.name,
        imageBase64: base64,
        mimeType: 'image/jpeg',
      });

      // Update local state
      const newPhoto: PickupPhoto = {
        pickupPhotoId: result.data.pickupPhotoId,
        signatureId,
        childId: '',
        familyId: '',
        personIndex: personIndex as 0 | 1 | 2,
        personName: person.name,
        photoUrl: result.data.photoUrl,
        storagePath: '',
        metadata: { uploadedAt: null as any, uploadedBy: '' },
      };

      setPhotos((prev) => {
        const next = new Map(prev);
        next.set(personIndex, newPhoto);
        return next;
      });
    } catch (err: any) {
      const msg = err?.message || t('parent.pickupPhotos.uploadError');
      setError(personIndex, msg);
    } finally {
      setUploading((prev) => {
        const next = new Set(prev);
        next.delete(personIndex);
        return next;
      });
      // Reset the input so the same file can be re-selected
      uploadRefs.current.get(personIndex)?.value !== undefined &&
        (uploadRefs.current.get(personIndex)!.value = '');
      cameraRefs.current.get(personIndex)?.value !== undefined &&
        (cameraRefs.current.get(personIndex)!.value = '');
    }
  }

  async function handleDelete(pickupPhotoId: string, personIndex: number) {
    setConfirmDeleteId(null);
    setDeleting((prev) => new Set(prev).add(pickupPhotoId));
    try {
      const deleteFn = httpsCallable<DeletePickupPhotoRequest, DeletePickupPhotoResponse>(
        functions,
        'deletePickupPhoto'
      );
      await deleteFn({ pickupPhotoId });
      setPhotos((prev) => {
        const next = new Map(prev);
        next.delete(personIndex);
        return next;
      });
    } catch (err: any) {
      const msg = err?.message || t('parent.pickupPhotos.deleteError');
      setError(personIndex, msg);
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(pickupPhotoId);
        return next;
      });
    }
  }

  if (!authorizedPersons || authorizedPersons.length === 0) return null;

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">📷</span>
        <div>
          <h3 className="font-medium text-gray-900">
            {t('parent.pickupPhotos.title')}
          </h3>
          <p className="text-xs text-gray-500">
            {t('parent.pickupPhotos.subtitle')}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {authorizedPersons.map((person, personIndex) => {
          const photo = photos.get(personIndex);
          const isUploading = uploading.has(personIndex);
          const isDeletingThis = photo && deleting.has(photo.pickupPhotoId);
          const errorMsg = errors.get(personIndex);

          return (
            <div key={personIndex} className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('parent.pickupPhotos.person')} {personIndex + 1}: {person.name}
                {person.relationship && (
                  <span className="text-gray-500 font-normal ml-1">
                    ({person.relationship})
                  </span>
                )}
              </p>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Thumbnail */}
                {photo ? (
                  <button
                    type="button"
                    onClick={() => setFullSizeUrl(photo.photoUrl)}
                    className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 border-blue-200 hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title={t('parent.pickupPhotos.viewFullSize')}
                  >
                    <img
                      src={photo.photoUrl}
                      alt={person.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ) : (
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <span className="text-xs text-gray-400 text-center leading-tight px-1">
                      {t('parent.pickupPhotos.noPhoto')}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {/* File upload button */}
                  <button
                    type="button"
                    className="btn btn-secondary text-xs py-1 px-2"
                    disabled={isUploading}
                    onClick={() => uploadRefs.current.get(personIndex)?.click()}
                  >
                    {isUploading
                      ? t('parent.pickupPhotos.uploading')
                      : t('parent.pickupPhotos.uploadButton')}
                  </button>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    ref={(el) => { if (el) uploadRefs.current.set(personIndex, el); }}
                    onChange={(e) => handleFileSelected(personIndex, e.target.files?.[0] ?? null)}
                  />

                  {/* Camera button — only shown on touch devices (mobile/tablet).
                      Uses the HTML5 <input capture="environment"> attribute which
                      opens the rear camera on mobile. On desktop the attribute is
                      ignored and behaves identically to the upload input, so we
                      hide the button there to avoid duplicate-looking controls. */}
                  {isTouchDevice && (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary text-xs py-1 px-2"
                        disabled={isUploading}
                        onClick={() => cameraRefs.current.get(personIndex)?.click()}
                      >
                        {t('parent.pickupPhotos.cameraButton')}
                      </button>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        ref={(el) => { if (el) cameraRefs.current.set(personIndex, el); }}
                        onChange={(e) => handleFileSelected(personIndex, e.target.files?.[0] ?? null)}
                      />
                    </>
                  )}

                  {/* Delete button */}
                  {photo && (
                    <button
                      type="button"
                      className="btn btn-danger text-xs py-1 px-2"
                      disabled={!!isDeletingThis}
                      onClick={() => setConfirmDeleteId(photo.pickupPhotoId)}
                    >
                      {isDeletingThis ? '...' : t('parent.pickupPhotos.removeButton')}
                    </button>
                  )}
                </div>
              </div>

              {/* Error message */}
              {errorMsg && (
                <p className="mt-2 text-xs text-red-600">{errorMsg}</p>
              )}

              {/* Confirm delete modal — inline */}
              {confirmDeleteId === photo?.pickupPhotoId && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 mb-2">
                    {t('parent.pickupPhotos.deleteConfirm')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn-danger text-xs py-1 px-3"
                      onClick={() => handleDelete(photo!.pickupPhotoId, personIndex)}
                    >
                      {t('parent.pickupPhotos.removeButton')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary text-xs py-1 px-3"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      {t('parent.pickupPhotos.cancelButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Full-size photo modal */}
      {fullSizeUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4"
          onClick={() => setFullSizeUrl(null)}
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="absolute -top-10 right-0 text-white text-3xl leading-none hover:text-gray-300"
              onClick={() => setFullSizeUrl(null)}
            >
              ×
            </button>
            <img
              src={fullSizeUrl}
              alt={t('parent.pickupPhotos.viewFullSize')}
              className="w-full rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
